import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserGoal } from '../api/userGoalApi';
import api from '../api/client';
import usePushNotifications from '../hooks/usePushNotifications';
import '../Style/onboarding.css';

const SUGGESTED_GOALS = [
  { title: 'Drink 2L Water Daily', difficulty: 1, category: 'HEALTH', emoji: '💧' },
  { title: 'Read 10 Pages', difficulty: 1, category: 'LEARNING', emoji: '📖' },
  { title: 'Morning Journal (5 min)', difficulty: 1, category: 'MINDSET', emoji: '✍️' },
  { title: '10,000 Steps', difficulty: 1, category: 'FITNESS', emoji: '🚶' },
  { title: 'Sleep by 11pm', difficulty: 1, category: 'HEALTH', emoji: '😴' },
  { title: 'No Phone First Hour', difficulty: 2, category: 'MINDSET', emoji: '📵' },
  { title: 'Cook Instead of Order', difficulty: 2, category: 'HEALTH', emoji: '🍳' },
  { title: '30 Min Exercise', difficulty: 2, category: 'FITNESS', emoji: '🏃' },
  { title: 'Meditate 10 Min', difficulty: 2, category: 'MINDSET', emoji: '🧘' },
  { title: 'Study 1 Hour', difficulty: 2, category: 'LEARNING', emoji: '📚' },
  { title: 'Cold Shower', difficulty: 3, category: 'FITNESS', emoji: '🚿' },
  { title: '90 Min Deep Work Block', difficulty: 3, category: 'PRODUCTIVITY', emoji: '⚡' },
  { title: 'Morning Gym Session', difficulty: 3, category: 'FITNESS', emoji: '🏋️' },
  { title: 'No Sugar', difficulty: 3, category: 'HEALTH', emoji: '🚫' },
  { title: '1 Hour Language Learning', difficulty: 3, category: 'LEARNING', emoji: '🌍' },
];

const DIFFICULTY_LABELS = {
  1: { label: 'Easy', color: '#73abff' },
  2: { label: 'Medium', color: '#ffca6e' },
  3: { label: 'Hard', color: '#fe6262' },
};

const DIFFICULTY_GROUPS = [1, 2, 3];

const FEATURES = [
  {
    emoji: '🏆',
    title: 'Compete & Inspire',
    description: 'Challenge friends and your partner — see who shows up every day.',
  },
  {
    emoji: '📅',
    title: 'Track Every Day',
    description: 'Log your habits daily and watch your consistency and XP grow.',
  },
  {
    emoji: '🌍',
    title: 'Go Global',
    description: 'View activity from the global community.',
  },
];

function WelcomeStep({ onNext }) {
  return (
    <div className="onboarding-page">
      <div className="onboarding-card onboarding-welcome-card">
        <div className="welcome-logo-wrap">
          <img
            src="https://placehold.co/160x52/1a1a2e/ffffff?text=GLOBAL+LEGENDS&font=montserrat"
            alt="Global Legends"
            className="welcome-logo"
          />
        </div>

        <h1 className="welcome-headline">
          Track habits.
          <br />
          Inspire each other.
          <br />
          <span className="welcome-headline-accent">Build consistency one day at a time.</span>
        </h1>

        <p className="welcome-sub">
          Global Legends is your daily accountability partner — for you, your friends, and your
          partner. Every rep counts. Every day of consistency matters.
        </p>

        <div className="welcome-features">
          {FEATURES.map((f) => (
            <div className="welcome-feature-card" key={f.title}>
              <span className="feature-emoji">{f.emoji}</span>
              <div>
                <p className="feature-title">{f.title}</p>
                <p className="feature-desc">{f.description}</p>
              </div>
            </div>
          ))}
        </div>

        <button className="onboarding-start-btn welcome-next-btn" onClick={onNext}>
          Get Started →
        </button>
      </div>
    </div>
  );
}

function GoalPickerStep({ selected, setSelected, onBack, onNext }) {
  const toggle = (title) => {
    setSelected((prev) =>
      prev.includes(title)
        ? prev.filter((t) => t !== title)
        : prev.length < 3
          ? [...prev, title]
          : prev,
    );
  };

  const handleStart = () => {
    onNext();
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-card">
        <button className="onboarding-back-btn" onClick={onBack}>
          ← Back
        </button>

        <div className="onboarding-header">
          <h1>Start with a few goals</h1>
          <p>Pick 1–3 goals to begin. You can always add more later.</p>
          {selected.length > 0 && (
            <span className="selected-count">{selected.length}/3 selected</span>
          )}
        </div>

        {DIFFICULTY_GROUPS.map((diff) => (
          <div key={diff} className="onboarding-group">
            <h3 className="onboarding-group-title" style={{ color: DIFFICULTY_LABELS[diff].color }}>
              {DIFFICULTY_LABELS[diff].label}
            </h3>

            <div className="onboarding-goal-grid">
              {SUGGESTED_GOALS.filter((g) => g.difficulty === diff).map((goal) => {
                const isSelected = selected.includes(goal.title);

                return (
                  <button
                    key={goal.title}
                    className={`onboarding-goal-chip ${isSelected ? 'selected' : ''}`}
                    style={{
                      borderColor: isSelected ? DIFFICULTY_LABELS[diff].color : 'transparent',
                      boxShadow: isSelected ? `0 0 0 2px ${DIFFICULTY_LABELS[diff].color}` : 'none',
                    }}
                    onClick={() => toggle(goal.title)}
                  >
                    <span className="chip-emoji">{goal.emoji}</span>
                    <span className="chip-title">{goal.title}</span>
                    {isSelected && <span className="chip-check">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="onboarding-footer">
          <button className="onboarding-start-btn" onClick={handleStart}>
            {selected.length > 0
              ? `Continue (${selected.length} goal${selected.length > 1 ? 's' : ''})`
              : 'Continue without starter goals'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationStep({ selected, onBack }) {
  const navigate = useNavigate();
  const { isSupported, isSubscribed, permissionState, loading, subscribe } = usePushNotifications();

  const [reminderTime, setReminderTime] = useState('20:00');
  const [isSaving, setIsSaving] = useState(false);
  const [goalsCreated, setGoalsCreated] = useState(false);

  const createSelectedGoalsOnce = async () => {
    if (goalsCreated) return;

    const selectedGoals = SUGGESTED_GOALS.filter((g) => selected.includes(g.title));

    await Promise.all(
      selectedGoals.map((g) => createUserGoal(`${g.emoji} ${g.title}`, g.difficulty, true, null)),
    );

    setGoalsCreated(true);
  };

  const finishOnboarding = async () => {
    await createSelectedGoalsOnce();
    localStorage.removeItem('showOnboarding');
    navigate('/');
  };

  const handleEnableNotifications = async () => {
    setIsSaving(true);

    try {
      if (isSupported && !isSubscribed && permissionState !== 'denied') {
        await subscribe();
      }

      await api.put('/api/notification-preferences', {
        notifyFollows: true,
        notifyReactions: true,
        notifyGoalCompleted: true,
        notifyRankUp: true,
        notifyAnnouncements: true,
        defaultReminderTime: reminderTime,
      });

      await finishOnboarding();
    } catch (err) {
      console.error('Failed to enable onboarding notifications:', err);
      alert('Could not finish onboarding. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = async () => {
    setIsSaving(true);

    try {
      await api.put('/api/notification-preferences', {
        notifyFollows: true,
        notifyReactions: true,
        notifyGoalCompleted: false,
        notifyRankUp: true,
        notifyAnnouncements: true,
        defaultReminderTime: null,
      });
    } catch (err) {
      console.error('Failed to save skipped notification preferences:', err);
    }

    try {
      await finishOnboarding();
    } catch (err) {
      console.error('Failed to finish onboarding:', err);
      alert('Could not finish onboarding. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-card">
        <button className="onboarding-back-btn" onClick={onBack}>
          ← Back
        </button>

        <div className="onboarding-header">
          <h1>Stay consistent</h1>
          <p>Turn on reminders for your daily goals.</p>
        </div>

        <div className="welcome-feature-card" style={{ marginBottom: '20px' }}>
          <span className="feature-emoji">🔔</span>
          <div>
            <p className="feature-title">Daily goal reminders</p>
            <p className="feature-desc">
              Pick a time and we’ll remind you if you still have goals left today.
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label className="form-label">Remind me daily at</label>
          <input
            className="input-field"
            type="time"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
          />
        </div>

        {!isSupported && (
          <p style={{ color: '#ef4444' }}>
            Push notifications are not supported in this browser. You can still use GLPT normally.
          </p>
        )}

        {permissionState === 'denied' && (
          <p style={{ color: '#ef4444' }}>
            Notifications are blocked. You can enable them later in your browser or device settings.
          </p>
        )}

        <div className="onboarding-footer">
          <button
            className="onboarding-start-btn"
            onClick={handleEnableNotifications}
            disabled={isSaving || loading}
          >
            {isSaving || loading ? 'Saving...' : 'Enable Reminders'}
          </button>

          <button className="onboarding-skip-btn" onClick={handleSkip} disabled={isSaving}>
            Skip — I’ll set this up later
          </button>
        </div>
      </div>
    </div>
  );
}
export default function OnboardingGoalPicker() {
  const [step, setStep] = useState('welcome');
  const [selectedGoals, setSelectedGoals] = useState([]);

  if (step === 'welcome') {
    return <WelcomeStep onNext={() => setStep('goals')} />;
  }

  if (step === 'goals') {
    return (
      <GoalPickerStep
        selected={selectedGoals}
        setSelected={setSelectedGoals}
        onBack={() => setStep('welcome')}
        onNext={() => setStep('notifications')}
      />
    );
  }

  return <NotificationStep selected={selectedGoals} onBack={() => setStep('goals')} />;
}
